import cx from "classnames";
import { debounce } from "lodash";
import { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";
import { useAppService } from "../hooks/use-app-service";
import imageActiveIcon from "../_assets/vectors/image-active-icon.svg";
import { Button } from "../_components/button.component";
import { CommunityTypeSelection } from "../_components/community/create-community/community-type-selection.component";
import { ErrorDiv } from "../_components/error.component";
import { Tick } from "../_components/form-controls/tick-component";
import { Loader } from "../_components/loader.component";
import { TextInput } from "../_components/text-input.component";
import {
  getCommunityAddress,
  MAIN_DOMAIN_SUFFIX,
} from "../_config/helper.config";
import { CommunityService } from "../_service";

export const CREATE_COMMUNITY_ROUTE = "/create-community";

function CreateCommunityPageComponent({ user }) {
  const [community, setCommunity] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [picture, setPicture] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingForAvailability, setIsCheckingForAvailability] =
    useState(false);
  const [isAddressAvailable, setIsAddressAvailable] = useState(false);
  const [selectedType, setSelectedType] = useState("paid");

  // community selection related
  const [createCommunityStep, setCreateCommunityStep] = useState(1);

  // debounced check for address availability
  const checkForAddressAvailability = useCallback(
    debounce((address) => {
      // if address is
      if (/^[A-Za-z0-9-]{3,63}$/.test(address)) {
        setIsCheckingForAvailability(true);
        // check for address
        CommunityService.checkForAddressAvailability(address, user)
          .then(({ addressAvailable }) => {
            // set if the address is available
            setIsAddressAvailable(addressAvailable);
            setIsCheckingForAvailability(false);
          })
          .catch((error) => {
            console.log({ error });
            setIsAddressAvailable(false);
            setIsCheckingForAvailability(false);
          });
      }
    }, 1000),
    []
  );

  // check if type selection is done or not, show it first if not done
  if (!selectedType) {
    return (
      <CommunityTypeSelection
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
    );
  }

  if (createCommunityStep === 1) {
    return (
      <CommunityDetailCollector
        picture={picture}
        setPicture={setPicture}
        name={name}
        setName={setName}
        address={address}
        setAddress={setAddress}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isCheckingForAvailability={isCheckingForAvailability}
        setIsCheckingForAvailability={setIsCheckingForAvailability}
        isAddressAvailable={isAddressAvailable}
        setIsAddressAvailable={setIsAddressAvailable}
        checkForAddressAvailability={checkForAddressAvailability}
        setCreateCommunityStep={setCreateCommunityStep}
      />
    );
  }

  // show last step
  return (
    <CommunityLayoutSelection
      user={user}
      name={name}
      address={address}
      picture={picture}
      selectedType={selectedType}
    />
  );
}

function AddressFAQ() {
  return (
    <div className="AddressFAQ">
      <span className="address-faq-button flex p-1 text-sm theme-bg-disable w-6 h-6 justify-center items-center rounded-full mx-2 cursor-pointer">
        i
      </span>
      <div className="address-faq theme-bg-s rounded shadow p-3 theme-text-subtitle-2 text-xxs font-light">
        <p>
          Address should be a combination of minimum 3 of each, letters, digits
          and hyphen
        </p>
        <p>Address cannot start or end with hyphen.</p>
        <p className="theme-text-danger my-2">
          No spaces, underscores, special characters are allowed
        </p>
        <p className="my-1 p-2 text-xxs theme-bg-default rounded">
          For instance if you choose address <b>rakeshteaches</b>, your
          community url will be <b>rakeshteaches{MAIN_DOMAIN_SUFFIX}</b>
        </p>
        <p className="my-1">Valid community address example:</p>
        <p className="text-right font-semibold theme-text-headline-1">
          community101
        </p>
        <p className="my-1">Invalid address example: </p>
        <p className="text-right font-semibold theme-text-headline-1">
          -some-new-community
        </p>
        <p className="text-right font-semibold theme-text-headline-1">---</p>
        <p className="text-right font-semibold theme-text-headline-1">
          -community 101-
        </p>
      </div>
    </div>
  );
}

function CommunityDetailCollector({
  picture,
  setPicture,
  name,
  setName,
  address,
  setAddress,
  typeSelection,
  setTypeSelection,
  isAddressAvailable,
  setIsAddressAvailable,
  isCheckingForAvailability,
  setIsCheckingForAvailability,
  checkForAddressAvailability,
  errorMessage,
  setErrorMessage,
  setCreateCommunityStep,
}) {
  return (
    <div
      className="CreateCommunityPage theme-bg-default"
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 899,
      }}>
      <div className="h-screen flex justify-center items-center">
        <div className="w-96">
          {
            <div className="card theme-bg-surface mx-2">
              <span className="theme-text-heading-2 text-lg font-semibold">
                Create Community
              </span>
              <PictureUpdate picture={picture} setPicture={setPicture} />
              <div className="font-semibold theme-text-heading-2 mt-4">
                Your Community Name
              </div>
              <TextInput
                placeholder="Enter here"
                validator={(value) => {
                  if (value.length < 2) {
                    return "Name invalid!";
                  }
                }}
                defaultValue={name}
                containerClassName="theme-bg-default"
                className="mb-4 py-2 focus:outline-none"
                onChange={(value) => {
                  setName(value);
                  // set address
                }}
              />
              <div className="font-semibold theme-text-heading-2 mt-4">
                Your Community Address
              </div>
              <div className="mb-4">
                <div className="flex">
                  <div className="w-1/2">
                    <TextInput
                      placeholder="URL"
                      validator={(value) => {
                        if (
                          !/^[A-Za-z0-9][A-Za-z0-9\-]{1,62}[A-Za-z0-9]$/.test(
                            value
                          )
                        ) {
                          return "URL invalid!";
                        } else {
                          setIsAddressAvailable(false);
                        }
                      }}
                      defaultValue={address}
                      className="py-2 focus:outline-none"
                      containerClassName="theme-bg-default"
                      inputHolderClassName={cx({
                        "border-red-500": !isAddressAvailable,
                        "border-green-500": isAddressAvailable,
                      })}
                      onChange={(value) => {
                        setAddress(value);
                        if (value.length > 2) {
                          if (!isCheckingForAvailability)
                            setIsCheckingForAvailability(true);
                        } else {
                          setIsCheckingForAvailability(false);
                        }
                        checkForAddressAvailability(value);
                      }}
                    />
                  </div>
                  <div className="w-1/2 p-2 font-semibold mt-3 flex items-start">
                    <span className="pr-4">{MAIN_DOMAIN_SUFFIX}</span>
                    <div className="flex items-center">
                      {isCheckingForAvailability ? (
                        <Loader />
                      ) : (
                        <Tick success={isAddressAvailable} />
                      )}
                      <AddressFAQ />
                    </div>
                  </div>
                </div>
              </div>
              <Button
                className="w-full py-2"
                onClick={() => {
                  // check if form can be submitted
                  const error = (() => {
                    if (name.length < 3) {
                      setErrorMessage(
                        "Community Name should be atleast 2 letter."
                      );
                      return true;
                    }

                    if (!/^[A-Za-z0-9-]{3,63}$/.test(address)) {
                      setErrorMessage(
                        "Address should only contain atleast 3 letters/numbers/hyphen"
                      );
                      return true;
                    }

                    if (!isAddressAvailable) {
                      setErrorMessage("Address not available!");
                      return true;
                    }

                    return false;
                  })();

                  if (error) {
                    // show error and return
                    return;
                  }

                  setCreateCommunityStep(2);
                }}
                // isLoading={isLoading}
                label="Next"
              />
              <ErrorDiv error={errorMessage} />
            </div>
          }
        </div>
      </div>
    </div>
  );
}

const CreateCommunityPage = connect((s) => ({ user: s.auth }))(
  CreateCommunityPageComponent
);

export { CreateCommunityPage };

function PictureUpdate({ picture = null, setPicture = () => {} }) {
  const pictureRef = useRef();

  let imageUrl = "";
  try {
    if (picture && typeof picture === "string" && picture.startsWith("http")) {
      imageUrl = picture;
    } else {
      imageUrl = URL.createObjectURL(picture);
    }
  } catch (error) {
    imageUrl = picture;
  }

  return (
    <div
      className="PictureUpdate"
      onClick={(e) => {
        pictureRef.current.click();
      }}>
      {/* file control */}
      <input
        className="hidden"
        type="file"
        ref={pictureRef}
        onChange={(e) => {
          setPicture(e.target.files[0]);
        }}
      />
      {picture ? (
        <div className="preview-control">
          <img src={imageUrl} alt="" />
        </div>
      ) : (
        <div className="upload-control">
          <img src={imageActiveIcon} alt="" />
          <div className="set-logo theme-text-primary font-semibold text-sm text-center pt-2">
            Set Logo
          </div>
        </div>
      )}
    </div>
  );
}

export const CommunityLayoutSelection = ({
  user,
  name,
  address,
  picture,
  selectedType,
}) => {
  const templates = [
    { name: "Online Course", emoji: "📚" },
    { name: "Mentoring", emoji: "🎤" },
    { name: "Brand & Fans", emoji: "🚀" },
    { name: "Professional Network", emoji: "🚁" },
    { name: "School/College Club", emoji: "🛵" },
    { name: "NGOs", emoji: "🙌" },
  ];
  const [community, setCommunity] = useState(null);
  const { analyticsService } = useAppService();

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState("");

  const onSelectHome = (value) => {
    setSelectedLayout(value);
  };

  if (community) {
    // if community is there, redirect to it in 3 secs
    window.location.href =
      `http://${getCommunityAddress(community)}?token=` + user.token;
  }
  return (
    <>
      {community ? (
        <div>You will be redirected shortly</div>
      ) : (
        <div className="PlanPage h-screen flex items-center justify-center text-center">
          <div>
            <div>
              <p className="text-md text-base">Create a Home</p>
              <p className="mt-4 w-3/4 sm:w-3/5 mx-auto text-sm theme-text-subtitle-2">
                Start from scratch or use any of our readymade templates. You
                can always edit them later or add/remove more channels.
              </p>
            </div>
            <div className="mt-4">
              <div
                onClick={() => onSelectHome("Create your own")}
                className={`border theme-border-default m-auto p-2 w-3/4 sm:w-1/2 text-left rounded cursor-pointer ${
                  selectedLayout === "Create your own"
                    ? "border theme-border-primary"
                    : "border theme-border-default"
                }`}>
                <span role="img" aria-label="emoji">
                  🛵
                </span>
                <span className="ml-2">Create your own</span>
              </div>
              <p className="mb-2 mt-2 py-6">Or pick a template to start with</p>
              {templates.map((tempData, index) => {
                return (
                  <div
                    key={index}
                    className={`m-auto p-2 w-3/4 sm:w-1/2 mb-3 mt-3 text-left rounded cursor-pointer ${
                      selectedLayout === tempData.name
                        ? "border theme-border-primary"
                        : "border theme-border-default"
                    }`}
                    onClick={() => onSelectHome(tempData.name)}>
                    {tempData.emoji}
                    <span className="ml-2">{tempData.name}</span>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Button
                className="w-3/4 sm:w-1/2 py-2 mx-auto "
                onClick={() => {
                  setIsLoading(true);
                  // call api to create community
                  CommunityService.createCommunity(
                    name,
                    address,
                    selectedLayout,
                    selectedType,
                    user
                  )
                    .then((response) => {
                      //mix panel
                      analyticsService.track("create-community", {
                        logo: picture ? "Yes" : "No",
                      });
                      analyticsService.track("template", {
                        templateName: selectedLayout,
                      });
                      // upload image if necessary
                      if (picture) {
                        CommunityService.updateLogo(
                          response.community.id,
                          user,
                          picture
                        )
                          .then((response) => {
                            setIsLoading(false);
                            setCommunity(response.community);
                          })
                          .catch((error) => {
                            setIsLoading(false);
                            if (
                              error &&
                              error.response &&
                              error.response.data.message
                            ) {
                              if (error.response.data.errors.logo) {
                                setErrorMessage(
                                  error.response.data.errors.logo[0]
                                );
                              }
                            } else {
                              setErrorMessage("Something went wrong!");
                            }
                          });
                      } else {
                        setIsLoading(false);
                        setCommunity(response.community);
                      }
                    })
                    .catch((error) => {
                      setIsLoading(false);
                      if (
                        error &&
                        error.response &&
                        error.response.data.message
                      ) {
                        if (error.response.data.errors.name) {
                          setErrorMessage(error.response.data.errors.name[0]);
                        }
                        if (error.response.data.errors.address) {
                          setErrorMessage(
                            error.response.data.errors.address[0]
                          );
                        }
                      } else {
                        setErrorMessage("Something went wrong!");
                      }
                    });
                }}
                isLoading={isLoading}
                label="Create Community"
              />
              <ErrorDiv error={errorMessage} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
